import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, RefreshCw, LogOut } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";

// Six segmented inputs for the 6-digit code. Auto-advances on type, supports
// paste of the full code, supports backspace to step back, and submits on
// Enter or when all six digits are filled.
function CodeInput({ value, onChange, disabled, onComplete }) {
  const refs = useRef([]);

  const setDigit = (i, ch) => {
    const cleaned = ch.replace(/\D/g, "").slice(0, 1);
    const next = value.split("");
    next[i] = cleaned;
    while (next.length < 6) next.push("");
    const v = next.slice(0, 6).join("");
    onChange(v);
    if (cleaned && i < 5) refs.current[i + 1]?.focus();
    if (v.length === 6 && !v.includes("")) onComplete?.(v);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !value[i] && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "Enter" && value.length === 6 && !value.includes("")) {
      e.preventDefault();
      onComplete?.(value);
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    onChange(text.padEnd(6, " ").slice(0, 6).replace(/ /g, ""));
    refs.current[Math.min(text.length, 5)]?.focus();
    if (text.length === 6) onComplete?.(text);
  };

  return (
    <div className="flex justify-center gap-2" data-testid="verify-code-input">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={value[i] || ""}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          className="h-14 w-12 rounded-lg border border-border bg-background text-center text-2xl font-[680] tracking-tight text-foreground shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
          data-testid={`verify-code-digit-${i}`}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  // Cooldown timer: 60s after a fresh send. Initial value is 60 because the
  // /register path issues a code automatically.
  const [cooldown, setCooldown] = useState(60);
  // Honest delivery state: false only when a send attempt did NOT leave the system.
  const [undelivered, setUndelivered] = useState(user?.verification_email_delivered === false);

  useEffect(() => {
    if (user?.verification_email_delivered === false) setUndelivered(true);
  }, [user]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // If a verified user lands here, send them onward.
  useEffect(() => {
    if (user?.email_verified) {
      navigate(user.account_complete ? "/dashboard" : "/welcome", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (raw) => {
    const c = (raw || code).replace(/\D/g, "");
    if (c.length !== 6) {
      toast.error("Enter all 6 digits.");
      return;
    }
    setVerifying(true);
    try {
      await api.post("/auth/verify-email", { code: c });
      toast.success("Email verified");
      await refreshUser();
      // refreshUser will trigger the useEffect above to navigate away.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify the code.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.delivered === false) {
        setUndelivered(true);
        toast.error("We couldn't deliver the email to that address.");
      } else {
        setUndelivered(false);
        toast.success("New code sent. Check your inbox.");
      }
      setCooldown(60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend the code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo size={44} /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-[0_8px_24px_rgba(16,24,40,0.06)]" data-testid="verify-email-card">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-50">
            <MailCheck className="h-6 w-6 text-primary" />
          </div>
          <h1 className="mt-4 text-center text-2xl font-[700] tracking-[-0.01em] text-foreground">Verify your email</h1>
          <p className="mt-2 text-center text-muted-foreground">
            We sent a 6-digit code to{" "}
            <span className="font-[560] text-foreground" data-testid="verify-email-target">{user?.email}</span>.
            Enter it below to activate your account.
          </p>

          <div className="mt-6">
            <CodeInput value={code} onChange={setCode} disabled={verifying} onComplete={submit} />
          </div>

          {undelivered && (
            <div className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                 data-testid="verify-email-undelivered">
              <p className="font-[640]">We couldn&apos;t deliver that email.</p>
              <p className="mt-0.5 text-xs">
                Your code is still valid, so if it turns up you can use it. Otherwise check the address is
                right — tap &quot;Use a different email&quot; below to sign up again with a corrected one —
                or resend once the timer clears. If it keeps failing, email hello@startupotter.com and
                we&apos;ll verify you manually.
              </p>
            </div>
          )}

          <Button
            className="mt-6 w-full"
            size="lg"
            disabled={verifying || code.replace(/\D/g, "").length !== 6}
            onClick={() => submit()}
            data-testid="verify-submit-button"
          >
            {verifying ? "Verifying…" : "Verify email"}
          </Button>

          <div className="mt-6 flex flex-col items-center gap-1.5 text-sm">
            <button
              onClick={resend}
              disabled={cooldown > 0 || resending}
              className="inline-flex items-center gap-1.5 font-[540] text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
              data-testid="verify-resend-button"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${resending ? "animate-spin" : ""}`} />
              {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Resend code"}
            </button>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              data-testid="verify-logout-button"
            >
              <LogOut className="h-3 w-3" /> Use a different email
            </button>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Didn&apos;t get the email? Check spam, or wait a minute and resend.
          Codes expire after 24 hours.
        </p>
      </div>
    </div>
  );
}
